import React, { useCallback } from 'react'

import { UserContext } from './UserContext'
import { userReducer } from './userReducer'
import { getUserTotalAudience, validateUserToken } from 'services'
import { UserState, UserActionTypes } from 'interfaces'
import { removeCookie, setCookie } from '@/shared/utils/pipes'
import { IUser } from '../../interfaces/user'
import { updateUserByEmail } from '../../services/user'
import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/nextjs'

interface Props {
  children: React.ReactNode
}

const User_INITAL_SATATE: UserState = {
  loading: true,
}

export const UserProvider: React.FC<Props> = ({ children }) => {
  const [state, dispatch] = React.useReducer(userReducer, User_INITAL_SATATE)
  const [fistValidationDonte, setFirstValidationDone] = React.useState(false)
  const {
    getAccessTokenSilently,
    isLoading,
    isAuthenticated,
    logout: auth0Logout,
    loginWithRedirect,
  } = useAuth0()

  const user = state.user

  React.useEffect(() => {
    const validateUserWithToken = async () => {
      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently()

          if (token) {
            setCookie('token', token, 60)
          }

          const user: IUser = await validateUserToken(token)
          if (user && user.id) {
            dispatch({ type: UserActionTypes.SET_USER, payload: user })
          }
        } catch (error) {
          console.error(error)

          // This code is to redirect user to the page that he was trying to access
          if (typeof window !== 'undefined') {
            // We vaidate if window exits to prevent errors in SSR
            const url = window?.location.href
            localStorage.setItem(
              'authParams',
              JSON.stringify({ urlCallback: url })
            )
          }
          loginWithRedirect()
          return // finally we return to prevent setFirstValidationDone to true and show home page
        }
      } else {
        dispatch({ type: UserActionTypes.SET_USER, payload: null })
        removeCookie('token')
      }
      setFirstValidationDone(true)
    }

    if (!isLoading) {
      validateUserWithToken()
    }
  }, [isLoading, isAuthenticated, loginWithRedirect, getAccessTokenSilently])

  const updateTotalAudience = useCallback(async () => {
    const result = await getUserTotalAudience(user.id)

    if ('error' in result) return

    dispatch({
      type: UserActionTypes.SET_USER,
      payload: { ...user, totalAudience: result.totalAudience },
    })
  }, [user])
  React.useEffect(() => {
    if (user?.id && user?.totalAudience === undefined) {
      updateTotalAudience()
    }
  }, [user, updateTotalAudience])

  React.useEffect(() => {
    if (fistValidationDonte) {
      dispatch({ type: UserActionTypes.SET_LOADING, payload: false })
    }
  }, [fistValidationDonte])

  React.useEffect(() => {
    dispatch({
      type: UserActionTypes.SET_IS_AUTHENTICATED,
      payload: !!state.user?.email,
    })
  }, [state.user])

  const setUser = (user: IUser) => {
    dispatch({ type: UserActionTypes.SET_USER, payload: user })
    Sentry.setUser(
      !user
        ? null
        : {
            id: user?.id,
            username: user?.usernameLower,
            avatar: user?.avatar,
          }
    )
  }

  const updateUserData = async (u?: IUser) => {
    const userData = { ...state.user, ...u }
    setUser(userData)

    const res = await updateUserByEmail(userData)
    return res?.id
  }

  const logout = useCallback(
    (skipRedirect?: boolean) => {
      removeCookie('token')

      auth0Logout()

      setUser(null)

      if (skipRedirect) {
        dispatch({ type: UserActionTypes.LOG_OUT })
        return
      }

      window.location.href = '/'
    },
    [auth0Logout]
  )

  return (
    <UserContext.Provider
      value={{
        ...state,
        isAuthenticated: !!state.user?.email,
        setUser,
        logout,
        updateUser: updateUserData,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}
